import {
  browserRandomInteger,
  type RandomIntegerSource,
} from '@/shared/random-integer'

export const DICE_SIDES = [4, 6, 8, 10, 12, 20, 100] as const
export const MAX_DICE_COUNT = 100
export const MAX_DICE_MODIFIER = 1_000_000
export const MAX_DICE_FORMULA_LENGTH = 32

export type DiceValidationError = 'empty_formula' | 'invalid_formula' | 'invalid_count' | 'unsupported_die' | 'invalid_modifier'
export type DiceError = DiceValidationError | 'processing_failed'

export interface DiceRequest {
  formula: string
}

export interface ParsedDiceFormula {
  count: number
  sides: (typeof DICE_SIDES)[number]
  modifier: number
  normalizedFormula: string
}

export type DiceValidation =
  | { status: 'ready'; value: ParsedDiceFormula }
  | { status: 'error'; error: DiceValidationError }

export type DiceRollResult =
  | { status: 'success'; formula: string; rolls: number[]; modifier: number; total: number }
  | { status: 'error'; error: DiceError }

export function formatDiceFormula(count: number, sides: number, modifier: number): string {
  const suffix = modifier === 0 ? '' : `${modifier > 0 ? '+' : ''}${modifier}`
  return `${count}d${sides}${suffix}`
}

export function syncDiceSettingsFromFormula(
  formula: string,
): Pick<ParsedDiceFormula, 'count' | 'sides' | 'modifier'> | null {
  const parsed = parseDiceFormula({ formula })
  if (parsed.status === 'error') return null

  const { count, sides, modifier } = parsed.value
  return { count, sides, modifier }
}

export function parseDiceFormula(request: DiceRequest): DiceValidation {
  const formula = request.formula.trim().replace(/ /g, '')
  if (formula === '') return { status: 'error', error: 'empty_formula' }
  if (formula.length > MAX_DICE_FORMULA_LENGTH) return { status: 'error', error: 'invalid_formula' }

  // Russian rulebooks write 3к6 or 3д6; a bare d20 means one die.
  const match = /^(\d*)[dдк](\d+)([+-]\d+)?$/i.exec(formula)
  if (!match) return { status: 'error', error: 'invalid_formula' }

  const count = match[1] === '' ? 1 : Number(match[1])
  const sides = Number(match[2])
  const modifier = match[3] === undefined ? 0 : Number(match[3])

  if (!Number.isSafeInteger(count) || count < 1 || count > MAX_DICE_COUNT) {
    return { status: 'error', error: 'invalid_count' }
  }
  if (!DICE_SIDES.some((supported) => supported === sides)) {
    return { status: 'error', error: 'unsupported_die' }
  }
  if (!Number.isSafeInteger(modifier) || Math.abs(modifier) > MAX_DICE_MODIFIER) {
    return { status: 'error', error: 'invalid_modifier' }
  }

  const supportedSides = sides as ParsedDiceFormula['sides']
  return {
    status: 'ready',
    value: {
      count,
      sides: supportedSides,
      modifier,
      normalizedFormula: formatDiceFormula(count, supportedSides, modifier),
    },
  }
}

export function rollDice(
  request: DiceRequest,
  randomInteger: RandomIntegerSource = browserRandomInteger,
): DiceRollResult {
  const validation = parseDiceFormula(request)
  if (validation.status === 'error') return validation

  const { count, sides, modifier, normalizedFormula } = validation.value
  try {
    const rolls = Array.from({ length: count }, () => randomInteger(sides) + 1)
    const total = rolls.reduce((sum, roll) => sum + roll, modifier)

    return { status: 'success', formula: normalizedFormula, rolls, modifier, total }
  } catch {
    return { status: 'error', error: 'processing_failed' }
  }
}
